import {HttpClient} from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {iReferenceDataDeliverablesGetAbi} from 'dex-abi/contracts/IReferenceDataDeliverablesGet';
import {priceStoreFacetAbi} from 'dex-abi/contracts/PriceStoreFacet';
import moment from 'moment';
import {defer, EMPTY, forkJoin, merge, mergeWith, NEVER, Observable, of, Subject} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {catchError, distinctUntilChanged, filter, map, repeat, takeUntil, tap} from 'rxjs/operators';
import {Hex} from 'viem';
import {toApiDateTime} from '../../common/dates';
import {destroy$} from '../../components/destroyable';
import {SessionService} from '../../components/dex/api-2/session.service';
import {WebsocketClientService} from '../../components/dex/api/websocket-client.service';
import {CommonBlockchainUtilService} from '../../components/dex/common-blockchain-util.service';
import {DexStreamingService} from '../../components/dex/dex-streaming/dex-streaming.service';
import {DexMdSubjectService} from '../../components/dex/dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {Deliverable, DeliverableId} from '../../components/dex/dex-streaming/types/deliverable';
import {HeldPosition} from '../../components/dex/dex-streaming/types/held-position';
import {RefRate} from '../../components/dex/dex-streaming/types/ref-rate';
import {Tranche, TrancheId} from '../../components/dex/dex-streaming/types/tranche';
import {vToDec} from '../../components/dex/dex-streaming/utils';
import {DynamicZyxDaemonService} from '../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {Widget} from '../../components/layout/widget/widget';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {FilterCriterion} from '../../models/filters/filter-criterion';
import {QueryParamFilterCriterion} from '../../models/filters/query-param-filter-criterion';
import {FloatWidgetFactory} from '../../pages/dashboard/float-layout/float-widget.factory';
import {AppConfigService} from '../../services/config.service';
import {DexTradeService} from '../dex-lp-trade-widget/trade-execution/dex-trade.service';
import {DEX_TRANSFER_WIDGET_TYPE_ID} from '../dex-transfer-widget/dex-transfer-widget-type-id';
import {DexTransferWidgetSettingsDef} from '../dex-transfer-widget/settings/dex-transfer-widget-settings';
import {DexDynWidget} from './dex-dyn-widget';
import {DexDynWidgetImplementation} from './dex-dyn-widget.implementation';

const cloneWithoutPrototypes = <T>(obj: T): T => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => cloneWithoutPrototypes(item)) as T;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime()) as T;
    }

    if (obj instanceof RegExp) {
        return new RegExp(obj) as T;
    }

    if (obj instanceof Map) {
        const copy = new Map();
        for (const [key, value] of obj) {
            copy.set(cloneWithoutPrototypes(key), cloneWithoutPrototypes(value));
        }
        return copy as T;
    }

    if (obj instanceof Set) {
        const copy = new Set();
        for (const item of obj) {
            copy.add(cloneWithoutPrototypes(item));
        }
        return copy as T;
    }

    const copy = Object.create(null);
    for (const key of Object.keys(obj)) {
        copy[key] = cloneWithoutPrototypes((obj as any)[key]);
    }

    for (const sym of Object.getOwnPropertySymbols(obj)) {
        copy[sym] = cloneWithoutPrototypes((obj as any)[sym]);
    }

    return copy;
};

@Component({
    selector:        'dex-dyn-widget',
    templateUrl:     './dex-dyn-widget.component.html',
    styleUrls:       ['./dex-dyn-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone:      false,
    viewProviders:   [
        SessionService,
        FloatWidgetFactory
    ],
    providers:       [
        DexTradeService
    ]
})
export class DexDynWidgetComponent implements OnInit {

    @ViewChild('loadingIndicator', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly destroy$ = destroy$();

    public readonly widgetImpl: DexDynWidgetImplementation = this.widget.implementationInstance;

    public dynRemote = this.dynamicZyxDaemonService.dynRemote;

    private readonly wsMsgStore = new Map<string, any[]>();
    private readonly wsMsgStore2 = new Map<string, any[]>();
    private readonly wsMsgStore3 = new Map<string, any[]>();
    private readonly wsMsgStoreOrders = new Map<string, any[]>();

    private readonly ws1MsgCounters: Array<[string, number]> = [];

    private readonly wsDebugEvents$ = new Subject<{key: string, data: any}>();

    constructor(
        @Inject(Widget)
        public widget: DexDynWidget,

        private changeDetectorRef: ChangeDetectorRef,
        private dynamicZyxDaemonService: DynamicZyxDaemonService,
        private appConfigService: AppConfigService,
        private websocketClientService: WebsocketClientService,
        private sessionService: SessionService,
        private dexStreamingService: DexStreamingService,
        private dexTradeService: DexTradeService,
        private floatWidgetFactory: FloatWidgetFactory,
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService,
        private dexMdSubjectService: DexMdSubjectService,
        private commonBlockchainUtilService: CommonBlockchainUtilService,
        private httpClient: HttpClient
    ) {
        (window as any).wsMsgStore = this.wsMsgStore; // TODO
        (window as any).ws1MsgCounters = this.ws1MsgCounters; // TODO
        (window as any).wsMsgStore2 = this.wsMsgStore2; // TODO
        (window as any).wsMsgStore3 = this.wsMsgStore3; // TODO
        (window as any).wsMsgStoreOrders = this.wsMsgStoreOrders; // TODO
        (window as any).wsDebugMethods = this.createWsDebugMethods(); // TODO
        this.dexTradeService.init(); // TODO
    }

    public ngOnInit(): void {
        this.dynamicZyxDaemonService.dynRemote$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(dynRemote => {
            if (dynRemote !== this.dynRemote) {
                this.dynRemote = dynRemote;
                this.changeDetectorRef.markForCheck();
            }
        });

        this.dexStreamingService.use(this.destroy$);

        console.warn(this.dexStreamingService); // TODO
    }

    public test(): void {
        // this.testBalance();
        this.printDeliverablesCount();
    }

    public testDynClient(): void {
        // this.dexTradeTestService.testDepositWithDiagnostics();
        this.dexTradeService.testDynClient();
    }

    public testNoWalletAuth(): void {
        this.connectDexApi2();
    }

    public testPosFrames(): void {
        this.doTestPosFrames();
    }

    public onTransfer(): void {
        this.floatWidgetFactory.createFloatWidget<DexTransferWidgetSettingsDef>(
            DEX_TRANSFER_WIDGET_TYPE_ID,
            null
        );
    }

    private async testBalance(): Promise<void> {
        const balance = await (this.dynRemote.primaryWallet$.value as any).getBalance();

        console.warn({balance, primaryWallet: this.dynRemote.primaryWallet$.value});
        console.warn({wallets: this.dynRemote.userWallets$.value});

        const balances = await Promise.all(
            this.dynRemote.userWallets$.value.map(async wallet => ({
                wallet,
                balance2: await wallet.getBalance()
            }))
        );
        console.warn(balances);
    }

    private async printDeliverablesCount(): Promise<void> {
        const dexContract = this.appConfigService.appConfig.dex.dexContractAddress as Hex;
        const publicClient = this.commonBlockchainUtilService.getPublicClient();

        const [
                  assetDeliverablesCount,
                  referentialAssetDeliverablesCount,
                  indexDeliverablesCount,
                  perpetualFutureDeliverablesCount
              ] = await publicClient.multicall({
            contracts: [
                {
                    address:      dexContract,
                    abi:          iReferenceDataDeliverablesGetAbi,
                    functionName: 'getAssetDeliverablesCount'
                },
                {
                    address:      dexContract,
                    abi:          iReferenceDataDeliverablesGetAbi,
                    functionName: 'getReferencingAssetDeliverablesCount'
                },
                {
                    address:      dexContract,
                    abi:          iReferenceDataDeliverablesGetAbi,
                    functionName: 'getIndexDeliverablesCount'
                },
                {
                    address:      dexContract,
                    abi:          iReferenceDataDeliverablesGetAbi,
                    functionName: 'getPerpetualFutureDeliverablesCount'
                },
            ]
        } as any);

        // TODO: Just for reference to the old code. Remove if the new one works.
        // const traderSigner = await this.dynRemote.getSigner(this.dynRemote.primaryWallet$.value);
        // const referenceDataDeliverables = iReferenceDataDeliverablesGetAbi.connect(dexContract, traderSigner);
        // const assetDeliverablesCount = await referenceDataDeliverables.getAssetDeliverablesCount();
        // const referentialAssetDeliverablesCount = await referenceDataDeliverables.getReferencingAssetDeliverablesCount();
        // const indexDeliverablesCount = await referenceDataDeliverables.getIndexDeliverablesCount();
        // const perpetualFutureDeliverablesCount = await referenceDataDeliverables.getPerpetualFutureDeliverablesCount();

        console.log("Deliverables Counts: asset [%s] referentialAsset [%s] index [%s] perpetualFuture [%s]",
            assetDeliverablesCount, referentialAssetDeliverablesCount, indexDeliverablesCount, perpetualFutureDeliverablesCount);
    }

    private async connectDexApi(): Promise<void> {
        const obs = new Observable(observer => {
            fromPromise(
                this.websocketClientService.connect({
                    url: 'wss://api.wss.sandbox.cplx.dev/v1/market_data/full/cyclical/bootstrap',
                    credentials: 'data_aggregator_2',
                    secret: 'f8afdeddab4d1da74349d0f058837bd5',
                    behaviour: 'simple',
                    heartbeatInterval: 15
                }).catch(error => {
                    console.error('Connection failed:', error);

                    return Promise.reject(error);
                })
            ).subscribe(observer);

            return () => this.websocketClientService.disconnect();
        });


        obs.pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private async connectDexApi2(): Promise<void> {
        const urlsDict = {
            hh:   {
                md:        'wss://hardhat.cplx.dev/v1/market_data/full/cyclical/bootstrap',
                portfolio: 'wss://hardhat.cplx.dev/v1/portfolio_data/cyclical/unified',
                hist:      'wss://hardhat.cplx.dev/v1/historical_data/cyclical/unified',
                ord:       'wss://hardhat.cplx.dev/v1/order_management/full/cyclical/unified'
            },
            test: {
                md:        'wss://test.cplx.dev/v1/market_data/full/cyclical/bootstrap',
                portfolio: 'wss://test.cplx.dev/v1/portfolio_data/cyclical/unified',
                hist:      'wss://test.cplx.dev/v1/historical_data/cyclical/unified',
                ord:       'wss://test.cplx.dev/v1/order_management/full/cyclical/unified'
            },
            uat:  {
                md:        'wss://uat.cplx.dev/v1/market_data/full/cyclical/bootstrap',
                portfolio: 'wss://uat.cplx.dev/v1/portfolio_data/cyclical/unified',
                hist:      'wss://uat.cplx.dev/v1/historical_data/cyclical/unified',
                ord:       'wss://uat.cplx.dev/v1/order_management/full/cyclical/unified'
            }
        } as const;
        const rec = this.appConfigService.appConfig.env.includes('monad')
            ? urlsDict.uat
            : urlsDict.hh;

        const obs = new Observable(observer => {
            const session = this.sessionService.createSimpleSession(
                rec.md,
                'market_data_1',
                '940f94c5a6017ad907d525a6e8f64c92'
            );

            const sessionSubscription = merge(
                session.onOpen$,
                session.onClose$,
                session.onError$,
                session.onMessage$.pipe(
                    tap(e => this.storeDexApiWsMessage(e))
                ),
                session.onCatchup$
            ).subscribe(observer);

            return () => {
                console.warn('unsub'); // TODO
                session.close();
                sessionSubscription.unsubscribe();
            };
        });

        const obs2 = new Observable(observer => {
            const session = this.sessionService.createSimpleSession(
                rec.portfolio,
                'portfolio_data_1',
                '139a30cad6f3200edb30b146965aa628'
            );

            const sessionSubscription = merge(
                session.onOpen$,
                session.onClose$,
                session.onError$,
                session.onMessage$.pipe(
                    tap(e => this.storeDexApiWsMessage2(e))
                ),
                session.onCatchup$
            ).subscribe(observer);

            return () => {
                console.warn('unsub'); // TODO
                session.close();
                sessionSubscription.unsubscribe();
            };
        });

        const obs3 = new Observable(observer => {
            const session = this.sessionService.createSimpleSession(
                rec.hist,
                'historical_data_1',
                '49f0d0ef52e999906e79bbe1cfbf15d7'
            );

            const sessionSubscription = merge(
                session.onOpen$,
                session.onClose$,
                session.onError$,
                session.onMessage$.pipe(
                    tap(e => this.storeDexApiWsMessage3(e))
                ),
                session.onCatchup$
            ).subscribe(observer);

            return () => {
                console.warn('unsub'); // TODO
                session.close();
                sessionSubscription.unsubscribe();
            };
        });

        const obs4 = new Observable(observer => {
            const session = this.sessionService.createSimpleSession(
                rec.ord,
                'order_management_1',
                'cca7f8e9aeaa1e4afd9db5959df03be2'
            );

            const sessionSubscription = merge(
                session.onOpen$,
                session.onClose$,
                session.onError$,
                session.onMessage$.pipe(
                    tap(e => this.storeDexApiWsMessageOrders(e))
                ),
                session.onCatchup$
            ).subscribe(observer);

            return () => {
                console.warn('unsub'); // TODO
                session.close();
                sessionSubscription.unsubscribe();
            };
        });

        merge(
            obs,
            obs2,
            obs3,
            obs4
        ).pipe(
            catchError(e => {
                console.error(e);
                return NEVER;
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private storeDexApiWsMessage(e: {ses: any, msg: Record<string, any>}): void {
        Object.keys(e.msg).forEach(key => {
            let rec = this.wsMsgStore.get(key);
            const msgData = cloneWithoutPrototypes(e.msg[key]); // Remove prototypes to simplify logs

            if (rec) {
                rec.push(msgData);
            } else {
                rec = [msgData];
                this.wsMsgStore.set(key, rec);
            }

            let counterRec = this.ws1MsgCounters.length
                ? this.ws1MsgCounters[this.ws1MsgCounters.length - 1]
                : undefined;

            if (counterRec && counterRec[0] === key) {
                counterRec[1] ++;
            } else {
                counterRec = [key, 1];
                this.ws1MsgCounters.push(counterRec);
            }

            this.wsDebugEvents$.next({key, data: msgData});
        });
    }

    private storeDexApiWsMessage2(e: {ses: any, msg: Record<string, any>}): void {
        Object.keys(e.msg).forEach(key => {
            let rec = this.wsMsgStore2.get(key);
            const msgData = cloneWithoutPrototypes(e.msg[key]); // Remove prototypes to simplify logs

            if (rec) {
                rec.push(msgData);
            } else {
                rec = [msgData];
                this.wsMsgStore2.set(key, rec);
            }

            this.wsDebugEvents$.next({key, data: msgData});
        });
    }

    private storeDexApiWsMessage3(e: {ses: any, msg: Record<string, any>}): void {
        Object.keys(e.msg).forEach(key => {
            let rec = this.wsMsgStore3.get(key);
            const msgData = cloneWithoutPrototypes(e.msg[key]); // Remove prototypes to simplify logs

            if (rec) {
                rec.push(msgData);
            } else {
                rec = [msgData];
                this.wsMsgStore3.set(key, rec);
            }

            this.wsDebugEvents$.next({key, data: msgData});
        });
    }

    private storeDexApiWsMessageOrders(e: {ses: any, msg: Record<string, any>}): void {
        Object.keys(e.msg).forEach(key => {
            let rec = this.wsMsgStoreOrders.get(key);
            const msgData = cloneWithoutPrototypes(e.msg[key]); // Remove prototypes to simplify logs

            if (rec) {
                rec.push(msgData);
            } else {
                rec = [msgData];
                this.wsMsgStoreOrders.set(key, rec);
            }

            this.wsDebugEvents$.next({key, data: msgData});
        });
    }

    private createWsDebugMethods(): any {

        interface DecoratedRefRate<T = any> {
            raw: T;
            pair: string;
            price: string;
            ts: string;
            dBase: Deliverable;
            dQuote: Deliverable;
            updateTs: number;
        }
        const decorateClpRefRate = (msgFromCpl: any, dBase: Deliverable, dQuote: Deliverable): DecoratedRefRate => ({
            raw: msgFromCpl,
            pair: [dBase.symbol, dQuote.symbol].join('/'),
            price: vToDec(msgFromCpl.price, dQuote.decimalPlaces).toString() + ` ${dQuote.symbol}`,
            ts: new Date(Number(BigInt(msgFromCpl.timestamp) / 1000n / 1000n)).toISOString(),
            dBase,
            dQuote,
            updateTs: Date.now()
        } satisfies DecoratedRefRate);

        const wsDebugMethods = {
            getLatestPosByPredicate: (predicate: (p: any) => boolean) => {
               const ps = this.wsMsgStore2.get('held_position');
               let foundPos: any = undefined;
               for (let i = ps.length - 1; i >= 0; i--) {
                   const p = ps[i];

                   if (predicate(p)) {
                       foundPos = p;
                       break;
                   }
               }

               return foundPos;
            },

            getPosKey: (p: any) => [
                p.identifier.position_id,
                p.identifier.account_id,
                p.identifier.deliverable_id
            ].join(':'),

            getLatestPosByPos: (pos: HeldPosition) => {
                return wsDebugMethods.getLatestPosByPredicate(
                    p =>
                        pos.identifier.positionId === p.identifier.position_id
                        && pos.identifier.accountId === p.identifier.account_id
                        && pos.identifier.deliverableId === p.identifier.deliverable_id
                );
            },

            getPositionsMapByKey: () => {
                const posMapByKey = new Map<string, any>();

                console.log('getPositionsMapByKey', {
                    ps: this.wsMsgStore2.get('held_position')
                }); // TODO

                for (const p of this.wsMsgStore2.get('held_position')) {
                    const key = wsDebugMethods.getPosKey(p);

                    posMapByKey.set(key, p);
                }

                return posMapByKey;
            },

            getPositionsByAccountId: (accountId: number) => {
                const posMapByKey = wsDebugMethods.getPositionsMapByKey();
                const result: any[] = [];

                for (const p of posMapByKey.values()) {
                    if (p.identifier.account_id === accountId) {
                        result.push();
                    }
                }

                return result;
            },

            getTranchesMapById: () => {
                const trMapByKey = new Map<string, any>();

                for (const tr of this.wsMsgStore.get('pool_tranche')) {
                    trMapByKey.set(tr.pool_tranche_id, tr);
                }

                return trMapByKey;
            },

            getTrancheDelRulesMapByTrancheId: () => {
                const ruleMapByTrId = new Map<TrancheId, Map<DeliverableId, any>>();
                const rs = this.wsMsgStore.get('pool_tranche_deliverable_rule');

                for (const r of rs) {
                    const trId = r.filter.pool_tranche_deliverable.pool_tranche_id;
                    let trRec = ruleMapByTrId.get(trId);

                    if (!trRec) {
                        trRec = new Map<DeliverableId, any[]>();
                        ruleMapByTrId.set(trId, trRec);
                    }

                    const delId = r.filter.pool_tranche_deliverable.deliverable_id;
                    let delRec = trRec.get(delId);

                    if (!delRec) {
                        delRec = [];
                        trRec.set(delId, delRec);
                    }

                    delRec.push(r);

                    if (r.rules.pool_tranche_participation_rules?.deposits_allowed) {
                        console.warn(r);
                    }
                }

                return ruleMapByTrId;
            },

            getPortfolioMap: () => {
                const mapByAcc = new Map<number, any>();
                const ms = this.wsMsgStore2.get('held_portfolio_health');

                for (const m of ms) {
                    mapByAcc.set(m.account_id, m);
                }

                return mapByAcc;
            },

            getRefRateMap: () => {
                let mapByPair = new Map<string, any>();
                const ms = this.wsMsgStore.get('reference_rate');

                ms.forEach(m => {
                    const dBase = this.dexSecuritiesSubjectService.getDeliverableById(Number(m.base_deliverable_id));
                    const dQuote = this.dexSecuritiesSubjectService.getDeliverableById(Number(m.quote_deliverable_id));
                    const pair = [dBase.symbol, dQuote.symbol].join('/');
                    const d = decorateClpRefRate(m, dBase, dQuote);
                    mapByPair.set(pair, d);
                });

                mapByPair = new Map(
                    [...mapByPair.entries()]
                        .sort((a, b) => a[0].localeCompare(b[0]))
                );

                return mapByPair;
            },

            subRates: (pair?: string) => {
                const dUsd = this.dexSecuritiesSubjectService.getDeliverableBySymbol('USD');

                if (!pair) {
                    const r = [...this.dexMdSubjectService.refRateMapByPair.values()]
                        .find(it => it.quoteDeliverableId === dUsd.deliverableId);
                    const dBase = this.dexSecuritiesSubjectService.getDeliverableById(r.baseDeliverableId);
                    const dQuote = this.dexSecuritiesSubjectService.getDeliverableById(r.quoteDeliverableId);

                    pair = [dBase.symbol, dQuote.symbol].join('/');
                }

                const [baseSymbol, quoteSymbol] = pair.split('/');
                const dBase = this.dexSecuritiesSubjectService.getDeliverableBySymbol(baseSymbol);
                const dQuote = this.dexSecuritiesSubjectService.getDeliverableBySymbol(quoteSymbol);
                const dBaseId = dBase.deliverableId;
                const dQuoteId = dQuote.deliverableId;

                const enum Source {
                    OUR_API  = 'OUR_API',
                    CLP_API  = 'CLP_API',
                    ON_CHAIN = 'ON_CHAIN'
                }

                let recentSource: Source;
                const mapBySource = new Map<Source, DecoratedRefRate>();

                const fromOurApi$ = this.dexMdSubjectService.getRefRateStream(dBaseId, dQuoteId).pipe(
                    map(r => ({
                        raw: r,
                        pair,
                        price: vToDec(r.price, dQuote.decimalPlaces).toString() + ` ${dQuote.symbol}`,
                        ts: new Date(Number(BigInt(r.timestamp) / 1000n / 1000n)).toISOString(),
                        dBase,
                        dQuote,
                        updateTs: Date.now()
                    } satisfies DecoratedRefRate<RefRate>)),
                    tap(r => {
                        const source = Source.OUR_API;
                        recentSource = source;
                        mapBySource.set(source, r);
                    })
                );

                const initFromClpApi = wsDebugMethods.getRefRateMap().get(pair);
                console.log({m: wsDebugMethods.getRefRateMap(), initFromClpApi}); // TODO
                const initFromClpApi$ = initFromClpApi ? of(initFromClpApi) : EMPTY;
                const fromClpApi$ = this.wsDebugEvents$.pipe(
                    filter(e => e.key === 'reference_rate'),
                    tap(e => console.log(e)), // TODO
                    map(e => e.data),
                    filter(m =>
                        m.base_deliverable_id === String(dBaseId)
                        && m.quote_deliverable_id === String(dQuoteId)
                    ),
                    map(m => decorateClpRefRate(m, dBase, dQuote)),
                    mergeWith(initFromClpApi$),
                    tap(r => {
                        const source = Source.CLP_API;
                        recentSource = source;
                        mapBySource.set(source, r);
                    })
                );

                const getFromOnChain = () => {
                    const publicClient = this.commonBlockchainUtilService.getPublicClient();

                    const pricePromise = publicClient.readContract({
                        address:           this.appConfigService.appConfig.dex.dexContractAddress as Hex,
                        abi:               priceStoreFacetAbi,
                        functionName:      'getUSDReferenceRateForDecimals',
                        args:              [
                            BigInt(dBase.deliverableId),
                            dUsd.decimalPlaces
                        ],
                        authorizationList: undefined
                    });

                    return fromPromise(pricePromise).pipe(
                        map(price => ({
                            raw: price,
                            pair: [dBase.symbol, dUsd.symbol].join('/'),
                            price: vToDec(price, dUsd.decimalPlaces).toString() + ` ${dUsd.symbol}`,
                            ts: new Date().toISOString(),
                            dBase,
                            dQuote: dUsd,
                            updateTs: Date.now()
                        } satisfies DecoratedRefRate<bigint>)),
                        tap(r => {
                            const source = Source.ON_CHAIN;
                            recentSource = source;
                            mapBySource.set(source, r);
                        })
                    );
                };

                const fromOnChain$ = defer(() => getFromOnChain()).pipe(
                    repeat({
                        delay: 1000
                    }),
                    distinctUntilChanged(((a, b) => a.price === b.price))
                );

                merge(fromOurApi$, fromClpApi$, fromOnChain$).pipe(
                    tap(recentR => {
                        const tss = [...mapBySource.values()]
                            .filter(r => r?.price === recentR?.price)
                            .map(r => r.updateTs);
                        const firstTs = tss.length > 1 ? Math.min(...tss) : tss[0];

                        const tableData = [
                            Source.OUR_API,
                            Source.CLP_API,
                            Source.ON_CHAIN
                        ].map(source => {
                            const r = mapBySource.get(source);

                            return {
                                recent: source === recentSource ? '*' : null,
                                delay: r ? `+${(r.updateTs - firstTs) / 1000} sec` : null,
                                source,
                                pair: r?.pair,
                                price: r?.price,
                                ts: r?.ts
                            };
                        });
                        console.table(tableData);
                        console.log([...mapBySource.values()]);
                    }),
                    takeUntil(this.destroy$)
                ).subscribe();
            },

            getPositionFramesMapByKey: () => {
                const posFramesMapByKey = new Map<string, any>();

                console.log('getPositionFramesMapByKey', {
                    pf: this.wsMsgStore2.get('held_position_frame')
                }); // TODO

                for (const pf of this.wsMsgStore2.get('held_position_frame')) {
                    const key = wsDebugMethods.getPosKey(pf);

                    let list = posFramesMapByKey.get(key);
                    if (!list) {
                        list = [];
                        posFramesMapByKey.set(key, list);
                    }

                    list.push(pf);
                }

                return posFramesMapByKey;
            }
        };

        return wsDebugMethods;
    }

    private doTestPosFrames(): void {
        const url = this.appConfigService.api.getPath('api/v1/position-frame/history');

        const requests: Array<Observable<any>> = [];

        interface Resp {
            data: unknown;
        }

        const trancheByAccId = [...this.dexMdSubjectService.trancheMapById.values()]
            .reduce((acc, tr) => {
                acc[tr.reservedAccountId] = tr;

                return acc;
            }, {} as Record<string, Tranche>);

        for (const pos of this.dexMdSubjectService.posMapById.values()) {
            const filters: FilterCriterion[] = [
                new QueryParamFilterCriterion({
                    queryParamName: 'accountId',
                    query: pos.identifier.accountId
                }),
                new QueryParamFilterCriterion({
                    queryParamName: 'deliverableId',
                    query: pos.identifier.deliverableId
                }),
                new QueryParamFilterCriterion({
                    queryParamName: 'positionId',
                    query: pos.identifier.positionId
                }),
                new QueryParamFilterCriterion({
                    queryParamName: 'from',
                    query: toApiDateTime(moment().subtract(30, 'days'))
                }),
                new QueryParamFilterCriterion({
                    queryParamName: 'count',
                    query: 30
                })
            ];
            const params = filters.reduce((acc, f) => {
                f.apply(acc);

                return acc;
            }, {});

            const tr = trancheByAccId[pos.identifier.accountId];

            const key = [
                this.dexSecuritiesSubjectService.getDeliverableById(tr.unitDeliverableId)?.symbol,
                this.dexSecuritiesSubjectService.getDeliverableById(pos.identifier.deliverableId)?.symbol,
                pos.identifier.positionId
            ].join(':');

            const req$ = this.httpClient.get<Resp>(url, {
                params
            }).pipe(
                map(resp => ({
                    key,
                    pos,
                    tr,
                    resp
                })),
                tap(r => console.log(r))
            );

            requests.push(req$);
        }

        if (requests.length) {
            forkJoin(requests).pipe(
                tap(rs => console.log(rs)),
                takeUntil(this.destroy$)
            ).subscribe();
        }
    }

}
